import _ from "lodash";
import { InputSelector } from "../../components/Forms/InputSelector";
import { IBaseFormProps } from "../../components/interfaces";
import { IField, screenTypes } from "../../utils/slideTemplateAllocation";
import { Dropdown, FieldName } from "../../components/Forms/Inputs";
import { slideHints } from "../../utils/slideHints";
import {
  IUploadLocations,
  MediaInput,
} from "../../components/Forms/FileHandling/MediaInput";
import { DurationField } from "./customComponents/DurationField";
import { fieldId } from "../../components/Forms/utils";

/**
 *
 * @param schema loaded schema
 * @param modelName name of model
 * @param itemData itemData state
 * @param setItemData setItemData state
 * @returns
 */
const SlidesForm = ({
  schema,
  modelName,
  itemData,
  setItemData,
  customModel,
  onSubmit,
}: IBaseFormProps): JSX.Element => {
  const updateItemData = (keyname: string, value: any) => {
    if (_.has(itemData, keyname)) {
      const newData = {
        ...itemData,
        [keyname]: value,
      };
      setItemData(newData);
    }
  };

  const updateMedia = (
    keyname: string,
    location: string,
    thumbLocation: string
  ) => {
    if (_.has(itemData, "videoThumbnail") && _.has(itemData, keyname)) {
      const newData = {
        ...itemData,
        [keyname]: location,
        videoThumbnail: thumbLocation,
      };
      setItemData(newData);
    }
  };

  const screenId = itemData.screenType as keyof typeof screenTypes;
  const getTemplateOptions = screenTypes[screenId]?.options || [];
  const getTemplates = screenTypes[screenId]?.templates || [];

  /**
   * Internal Template Select Construction
   */
  const TemplateSelect = () => (
    <div className={"mb-2 space-y-1"}>
      <FieldName
        name="Template"
        hint="Select one of the templates available for this screen"
      />
      <Dropdown
        value={itemData.templateType}
        onChange={(e) => {
          updateItemData("templateType", e.target.value);
        }}
        options={getTemplateOptions}
        modelname={modelName}
        required
        name="templateType"
        type="dropdown"
      />
    </div>
  );

  const templateId = itemData.templateType as keyof typeof getTemplates;

  const getFieldNames =
    getTemplates[templateId]?.map((field: IField) => field.field) || [];

  const getFieldData =
    getTemplates[templateId]?.reduce((obj: any, field: any) => {
      return {
        ...obj,
        [field.field]: _.omit(field, "field"),
      };
    }, {}) || {};

  const filteredSchema = schema
    .filter((field) => _.includes(getFieldNames, field.name))
    .map((field) => {
      return {
        ...field,
        ...getFieldData[field.name],
      };
    });

  const templateName = getTemplateOptions[templateId]?.label;

  return (
    <form onSubmit={onSubmit} id="base-form">
      <div className="flex flex-col gap-4">
        <TemplateSelect />
        {filteredSchema.map((item, index) => {
          const checkHint =
            slideHints.find(
              (h) =>
                h.field === item.name &&
                _.includes(h.screenIds, itemData.screenType) &&
                _.includes(h.templates, templateName)
            )?.hint || undefined;

          const isMedia = item.type === "image" || item.type == "video";
          const isDuration = item.name === "duration";

          return isDuration ? (
            <DurationField
              id={fieldId(item.name)}
              key={index}
              value={itemData[item.name]}
              name={item.display}
              type="number"
              onChange={(e) =>
                updateItemData(item.name, Math.max(0, e.target.valueAsNumber))
              }
            />
          ) : isMedia ? (
            <MediaInput
              key={index}
              type={item.type}
              name={item.display}
              value={itemData[item.name]}
              thumbUrl={itemData.videoThumbnail}
              onChange={(e: IUploadLocations) => {
                updateMedia(
                  item.name,
                  e.fileLocation || "",
                  e.thumbLocation || ""
                );
              }}
              required={item.required}
              hint={checkHint}
            />
          ) : (
            <InputSelector
              key={index}
              value={itemData[item.name]}
              onChange={(e) => {
                updateItemData(
                  item.name,
                  item.type === "number"
                    ? e.target.valueAsNumber
                    : item.type === "bool"
                    ? e.target.checked
                    : e.target.value
                );
              }}
              overrideName={item.display}
              overrideRequired={item.required}
              overrideHint={checkHint}
              modelname={modelName}
              customModel={customModel}
              {...item}
            />
          );
        })}
      </div>
    </form>
  );
};

export default SlidesForm;
